import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { FilterRule, FilterAction } from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { FilterRuleBuilder } from '../../components/filters/FilterRuleBuilder'

interface EmailFilter {
  id: number
  name: string
  conditions: FilterRule
  action: FilterAction
  actionTarget: string | null
  priority: number
  active: boolean
  isPreset: boolean
  createdAt: string
}

interface SimulationResult {
  evaluations: {
    id: number
    name: string
    matched: boolean
    action: FilterAction
    actionTarget: string | null
  }[]
  match: { id: number; name: string; action: FilterAction; actionTarget: string | null } | null
  outcome: string
}

const ACTION_LABELS: Record<FilterAction, string> = {
  create_task: 'Creează task',
  route_module: 'Rutează la modul',
  inbox: 'Lasă în inbox',
  archive: 'Arhivează',
  block: 'Nu ajunge în CRM',
  assign_direct: 'La departamentul unei persoane',
}

/** Ce înseamnă fiecare acțiune, în cuvintele omului care se uită la ecran. */
const ACTION_HINTS: Partial<Record<FilterAction, string>> = {
  block:
    'Emailul nu devine sarcină și nu se mai vede în CRM. Rămâne în căsuța ta de email — filtrul lucrează doar pe copia noastră, nu pe serverul de mail.',
  assign_direct:
    'Task-ul ajunge la „De preluat” în departamentul persoanei din câmpul următor (departamentul se deduce din rolul ei) și îl ia cine are timp de acolo. Termen doar dacă emailul spune unul.',
  create_task:
    'Task-ul ajunge la „De preluat” în departamentul scris în câmpul următor și îl ia cine are timp de acolo.',
}

const SAMPLE_EMAILS = [
  {
    label: 'Comandă Dr.Max',
    from: 'comenzi@dr-max.ro',
    to: 'comenzi@boost4life.ro',
    subject: 'Comanda noua PO#1234',
    body: 'va rugam procesati comanda',
  },
  {
    label: 'Eroare ANAF SPV',
    from: 'spv@anaf.ro',
    to: 'contabilitate@boost4life.ro',
    subject: 'Eroare la validare factura electronica',
    body: 'mesajul a esuat',
  },
  {
    label: 'Răspuns tipografie',
    from: 'office@tipografia.ro',
    to: 'comenzi@boost4life.ro',
    subject: 'Re: [B4L-PRINT-000023] Comanda confirmata',
    body: 'comanda este in productie',
  },
  {
    label: 'Newsletter spam',
    from: 'noreply@somefunky.xyz',
    to: 'contact@boost4life.ro',
    subject: 'Best deals on viagra',
    body: 'click here to subscribe',
  },
  {
    label: 'Comandă eMAG',
    from: 'noreply@emag.ro',
    to: 'comenzi@boost4life.ro',
    subject: 'Comanda noua marketplace #ML99',
    body: 'va rugam procesati',
  },
  {
    label: 'Newsletter agenție',
    from: 'office@agentie-marketing.ro',
    to: 'contact@boost4life.ro',
    subject: 'Newsletter saptamanal',
    body: 'click here to unsubscribe',
  },
]

const EMPTY_RULE: FilterRule = { field: 'subject', match: 'contains', value: '' }

export function SetariFiltreEmailPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.filters:write')

  const [editTarget, setEditTarget] = useState<EmailFilter | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [simOpen, setSimOpen] = useState(false)

  const query = useQuery<{ filters: EmailFilter[] }>({
    queryKey: ['filters'],
    queryFn: async () => (await api.get<{ filters: EmailFilter[] }>('/settings/filters')).data,
  })
  const filters = useMemo(() => query.data?.filters ?? [], [query.data])

  const createMutation = useMutation({
    mutationFn: async (data: Partial<EmailFilter>) =>
      (await api.post('/settings/filters', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filters'] })
      toast.success('Filtru creat')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<EmailFilter> }) =>
      (await api.patch(`/settings/filters/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filters'] })
      toast.success('Actualizat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/settings/filters/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filters'] })
      toast.success('Șters')
    },
  })

  const seedMutation = useMutation({
    mutationFn: async () => (await api.post('/settings/filters/seed-presets')).data,
    onSuccess: (data: { count: number }) => {
      queryClient.invalidateQueries({ queryKey: ['filters'] })
      toast.success(`${data.count} filtre predefinite readuse la forma inițială`)
    },
    onError: showError,
  })

  const toggleActive = (f: EmailFilter) =>
    updateMutation.mutate({ id: f.id, data: { active: !f.active } })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Filtre email</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setSimOpen(true)}>
            Simulator
          </Button>
          {canWrite && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  if (
                    confirm(
                      'Readuci cele 8 filtre predefinite la forma inițială? Filtrele create de tine rămân neatinse.',
                    )
                  )
                    seedMutation.mutate()
                }}
                loading={seedMutation.isPending}
              >
                Readu filtrele predefinite
              </Button>
              <Button onClick={() => setCreateOpen(true)}>+ Filtru nou</Button>
            </>
          )}
        </div>
      </div>

      <Card description="Filtrele se aplică în ordinea priorității (numărul mai mic = evaluat primul); primul filtru care se potrivește câștigă.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : filters.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun filtru. {canWrite && 'Apasă „Readu filtrele predefinite” sau „+ Filtru nou”.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Prioritate</th>
                <th className="py-2 pr-3 font-medium">Nume</th>
                <th className="py-2 pr-3 font-medium">Acțiune</th>
                <th className="py-2 pr-3 font-medium">Tip</th>
                <th className="py-2 pr-3 font-medium">Activ</th>
                {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {filters.map((f) => (
                <tr key={f.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{f.priority}</td>
                  <td className="py-2 pr-3">
                    {f.name}
                    {f.isPreset && (
                      <span className="ml-2 rounded bg-b4l-purple-light px-1.5 py-0.5 text-[10px] text-b4l-purple-dark">
                        predefinit
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {ACTION_LABELS[f.action]}
                    {f.actionTarget && (
                      <span className="text-b4l-text-muted"> → {f.actionTarget}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs">{describeRule(f.conditions)}</td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => canWrite && toggleActive(f)}
                      disabled={!canWrite}
                      className={
                        f.active
                          ? 'rounded bg-b4l-green-light px-2 py-0.5 text-[10px] text-b4l-green-dark'
                          : 'rounded bg-b4l-bg px-2 py-0.5 text-[10px] text-b4l-text-muted'
                      }
                    >
                      {f.active ? 'activ' : 'inactiv'}
                    </button>
                  </td>
                  {canWrite && (
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => setEditTarget(f)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Editează
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Ștergi filtrul „${f.name}”?`)) deleteMutation.mutate(f.id)
                          }}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Șterge
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <FilterFormModal
          title="Filtru nou"
          submitLabel="Creează"
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <FilterFormModal
          title={`Editează ${editTarget.name}`}
          submitLabel="Salvează"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}

      <SimulatorModal open={simOpen} onClose={() => setSimOpen(false)} />
    </div>
  )
}

function describeRule(rule: FilterRule): string {
  if ('operator' in rule) {
    return `${rule.operator} (${rule.rules.length} conditii)`
  }
  return `${rule.field} ${rule.match} "${rule.value.slice(0, 30)}${rule.value.length > 30 ? '…' : ''}"`
}

interface FormProps {
  title: string
  submitLabel: string
  initial?: EmailFilter
  onClose: () => void
  onSubmit: (data: Partial<EmailFilter>) => void
  submitting: boolean
}

function FilterFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [action, setAction] = useState<FilterAction>(initial?.action ?? 'create_task')
  const [actionTarget, setActionTarget] = useState(initial?.actionTarget ?? '')
  const [priority, setPriority] = useState(initial?.priority ?? 100)
  const [conditions, setConditions] = useState<FilterRule>(initial?.conditions ?? EMPTY_RULE)

  const submit = () => {
    if (!name.trim()) {
      toast.error('Denumire obligatorie')
      return
    }
    onSubmit({
      name,
      action,
      actionTarget: actionTarget || null,
      priority,
      conditions,
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Nume" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Acțiune</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as FilterAction)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
            >
              {Object.entries(ACTION_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <Field
            label={action === 'assign_direct' ? 'Emailul persoanei' : 'Țintă (departament / modul)'}
            value={actionTarget}
            onChange={(e) => setActionTarget(e.target.value)}
            hint={
              action === 'assign_direct'
                ? 'Ex.: contabilitate@boost4life.ro. Dacă emailul nu duce la niciun utilizator, task-ul rămâne în departamentul în care l-a clasificat extractorul — o regulă greșită nu trebuie să facă munca să dispară.'
                : 'Pentru „Creează task": departamentul (contabilitate, vanzari, marketing, depozit, administrativ). Pentru „Rutează la modul": numele modulului.'
            }
          />
        </div>
        {ACTION_HINTS[action] && (
          <p className="rounded-lg bg-b4l-bg p-2 text-xs text-b4l-text-muted">
            {ACTION_HINTS[action]}
          </p>
        )}
        <Field
          label="Prioritate (mic = primul evaluat)"
          type="number"
          value={priority}
          onChange={(e) => setPriority(Number(e.target.value) || 100)}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Condiții</label>
          <FilterRuleBuilder rule={conditions} onChange={setConditions} isRoot />
        </div>
      </div>
    </Modal>
  )
}

function SimulatorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [from, setFrom] = useState('comenzi@dr-max.ro')
  const [to, setTo] = useState('comenzi@boost4life.ro')
  const [subject, setSubject] = useState('Comanda noua PO#1234')
  const [body, setBody] = useState('va rugam procesati comanda')
  const [result, setResult] = useState<SimulationResult | null>(null)

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post<SimulationResult>('/settings/filters/simulate', { from, to, subject, body }))
        .data,
    onSuccess: setResult,
    onError: showError,
  })

  const loadSample = (s: (typeof SAMPLE_EMAILS)[number]) => {
    setFrom(s.from)
    setTo(s.to)
    setSubject(s.subject)
    setBody(s.body)
    setResult(null)
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        setResult(null)
        onClose()
      }}
      title="Simulator filtre"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            Testează
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
            Exemple preîncărcate
          </label>
          <div className="flex flex-wrap gap-1">
            {SAMPLE_EMAILS.map((s) => (
              <button
                key={s.label}
                onClick={() => loadSample(s)}
                className="rounded bg-b4l-bg px-2 py-1 text-[11px] hover:bg-b4l-blue-light"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="De la" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Field label="Către" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Field label="Subiect" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Conținut</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>

        {result && (
          <div className="space-y-2 rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <p className="text-sm font-medium">{result.outcome}</p>
            <details className="text-xs">
              <summary className="cursor-pointer text-b4l-text-muted">
                Vezi toate filtrele evaluate ({result.evaluations.length})
              </summary>
              <ul className="mt-1 space-y-0.5">
                {result.evaluations.map((e) => (
                  <li
                    key={e.id}
                    className={
                      e.matched ? 'font-mono text-b4l-green-dark' : 'font-mono text-b4l-text-dim'
                    }
                  >
                    {e.matched ? '✓' : '×'} {e.name} → {ACTION_LABELS[e.action]}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
